// Illustrative data for the homepage product previews. Labels, statuses, and
// offer fields mirror the real advisor portal and submit-offer form; the
// values are invented and every preview is captioned as sample data.

/** Subset of advisor-portal status labels (advisor-portal-v2.component.ts). */
export type PortalStatus =
  'New Request' | 'Terms Delivered' | 'Offer Selected' | 'In Processing' | 'Funded';

export interface SampleRequest {
  ref: string;
  client: string;
  loanType: string;
  amount: number;
  status: PortalStatus;
  submitted: string;
}

export interface OfferOption {
  label: string;
  interestRate: string;
  loanTerm: string;
  termType: string;
  interestOnlyPeriod: string;
  prepaymentPenalty: string;
  estimatedPoints: string;
}

/** Status dot colors. Color lives inside product UI only, as in the real portal. */
export const STATUS_DOT: Readonly<Record<PortalStatus, string>> = {
  'New Request': 'bg-amber-400',
  'Terms Delivered': 'bg-emerald-400',
  'Offer Selected': 'bg-sky-400',
  'In Processing': 'bg-orange-400',
  Funded: 'bg-emerald-500',
};

export const SAMPLE_REQUESTS: readonly SampleRequest[] = [
  {
    ref: 'CP-1048',
    client: 'Okafor Family Trust',
    loanType: 'Residential Real Estate Mortgage',
    amount: 742500,
    status: 'Terms Delivered',
    submitted: 'Oct 2',
  },
  {
    ref: 'CP-1047',
    client: 'Lindqvist Holdings LLC',
    loanType: 'Commercial Real Estate Mortgage',
    amount: 2365000,
    status: 'Offer Selected',
    submitted: 'Oct 1',
  },
  {
    ref: 'CP-1045',
    client: 'Priya Raman',
    loanType: 'Securities Backed Line of Credit',
    amount: 1180000,
    status: 'New Request',
    submitted: 'Sep 30',
  },
  {
    ref: 'CP-1041',
    client: 'Castellano Dental Group',
    loanType: 'Commercial Business Solutions',
    amount: 460000,
    status: 'In Processing',
    submitted: 'Sep 26',
  },
  {
    ref: 'CP-1036',
    client: 'Whitaker & Moss Advisory',
    loanType: 'Acquisition Loans for Financial Advisors',
    amount: 3100000,
    status: 'Funded',
    submitted: 'Sep 18',
  },
];

/** Field labels match the residential section of the submit-offer form. */
export const OFFER_FIELDS: ReadonlyArray<{ key: keyof Omit<OfferOption, 'label'>; label: string }> =
  [
    { key: 'interestRate', label: 'Interest Rate' },
    { key: 'loanTerm', label: 'Loan Term' },
    { key: 'termType', label: 'Term Type' },
    { key: 'interestOnlyPeriod', label: 'Interest-Only Period' },
    { key: 'prepaymentPenalty', label: 'Prepayment Penalty' },
    { key: 'estimatedPoints', label: 'Estimated Points' },
  ];

export const SAMPLE_OPTIONS: readonly OfferOption[] = [
  {
    label: 'Option 1',
    interestRate: '6.125%',
    loanTerm: '30 years',
    termType: 'Fixed',
    interestOnlyPeriod: 'None',
    prepaymentPenalty: 'None',
    estimatedPoints: '1.0%',
  },
  {
    label: 'Option 2',
    interestRate: '5.875%',
    loanTerm: '30 years',
    termType: 'ARM',
    interestOnlyPeriod: 'None',
    prepaymentPenalty: 'None',
    estimatedPoints: '0.5%',
  },
  {
    label: 'Option 3',
    interestRate: '5.750%',
    loanTerm: '15 years',
    termType: 'Fixed',
    interestOnlyPeriod: 'None',
    prepaymentPenalty: 'None',
    estimatedPoints: '1.25%',
  },
];

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatUsd(value: number): string {
  return USD.format(value);
}
